import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import {
  SOB_LABEL,
  TABLE_HIERARCHY_FIELD,
  TABLE_KEY_FIELD,
} from 'legacy/lut/components/widgets/public/partner360/competitorsSob/configs'
import { getExpandedKeys } from 'legacy/lut/components/widgets/public/partner360/competitorsSob/helpers/getExpandedKeys'
import { getVisibleItems } from 'legacy/lut/components/widgets/public/partner360/competitorsSob/helpers/getVisibleItems'
import { MultiSelect } from 'legacy/lut/library/multiSelect'
import { ReactElement, useCallback, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Switch } from 'core-components/library/checkbox'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { FinancialTable } from 'partner360/components/financialTable'
import { OTHER_COMPETITOR_ID, sobFields } from 'partner360/features/competitorsSob/constants'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { ICompetitorsSobChangeLog } from 'partner360/models/competitorsSob/ICompetitorsSobChangeLog'
import { ICompetitor } from 'partner360/models/ICompetitor'
import { ISelectedCompetitor } from 'partner360/models/ISelectedCompetitor'
import { ISobCompetitorsGroup } from 'partner360/models/ISobCompetitorsGroup'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { SobCompetitorsCharts } from './components/sobCompetitorsCharts/SobCompetitorsCharts'
import { getColumns } from './helpers/getColumns'
import { TRANSLATION_KEYS } from './translations'

import styles from './CompetitorsSob.module.scss'

interface IConfirmDeleteCompetitorDialog {
  isShow: boolean
  newItems: string[]
}

interface ISaveControlParams {
  isDisabledSave?: boolean
}

interface IProps {
  data: ISobCompetitorsGroup[]
  competitorsList?: ICompetitor[]
  selectedCompetitors?: ISelectedCompetitor[]
  onChangeSelectedCompetitors?: (value: string[]) => void
  onChangeTable?: (entryId: string, competitorId: string, field: string) => (value: number) => void
  onCollapse: (keyField: string, keyFieldValue: string | number, hierarchyField: string) => void
  onExpand: (keyField: string, keyFieldValue: string | number, hierarchyField: string) => void
  selectedPeriodFQuarters: number[]
  className?: string
  fYear: number
  currency: string
  netNdp: ActualsValueTypes
  title?: string
  additionalControls?: ReactElement
  saveControl?: (params: ISaveControlParams) => ReactElement
  tableBanner?: ReactElement
  changes?: ICompetitorsSobChangeLog[]
  isEditableCompetitorSelect?: boolean
}

export const CompetitorsSob = ({
  data,
  competitorsList = [],
  selectedCompetitors = [],
  onChangeSelectedCompetitors,
  onChangeTable,
  onCollapse,
  onExpand,
  selectedPeriodFQuarters,
  className,
  fYear,
  title,
  additionalControls,
  saveControl,
  tableBanner,
  changes = [],
  isEditableCompetitorSelect = true,
}: IProps) => {
  const [confirmDeleteCompetitorDialog, setConfirmDeleteCompetitorDialog] =
    useState<IConfirmDeleteCompetitorDialog>({ isShow: false, newItems: [] })

  const [isDisplaySellThru, setIsDisplaySellThru] = useState(false)

  const isShowCompetitorsSelect = useMemo(() => {
    return Boolean(onChangeSelectedCompetitors)
  }, [onChangeSelectedCompetitors])

  const expandedKeys = useMemo(() => {
    return getExpandedKeys(data)
  }, [data])

  const visibleItems = useMemo(() => {
    return getVisibleItems(data, expandedKeys)
  }, [data, expandedKeys])

  const columns = useMemo(() => {
    return getColumns({
      onChange: onChangeTable,
      otherKey: OTHER_COMPETITOR_ID,
      expandedKeys,
      isDisplaySellThru,
      selectedPeriodFQuarters,
      changes,
    })
  }, [onChangeTable, expandedKeys, isDisplaySellThru, selectedPeriodFQuarters, changes])

  const competitorsSource = useMemo(() => {
    return new Crud({
      fetcher: new Static(competitorsList),
      keyField: 'CompetitorID',
      defaultPaging: { page: 0, pageSize: competitorsList.length },
    })
  }, [competitorsList])

  const headerGroups = useMemo(() => {
    const previousFYear = fYear - 1

    return [
      { name: SOB_LABEL, colspan: 2, withoutBorder: true },
      { name: previousFYear.toString(), colspan: 5 },
      { name: fYear.toString(), colspan: 5 },
    ]
  }, [fYear])

  const isOtherSobLessThan0 = useMemo(() => {
    const items = visibleItems.flatMap((el) => el.Items)
    return items
      .filter((el) => el.CompetitorID === OTHER_COMPETITOR_ID)
      .some((el) => sobFields.some((field) => el[field] < 0))
  }, [visibleItems])

  const onChangeCompetitorsList = useCallback(
    (values: string[]) => {
      if (!onChangeSelectedCompetitors) {
        return
      }
      if (values.length < selectedCompetitors.length) {
        setConfirmDeleteCompetitorDialog({ isShow: true, newItems: values })
        return
      }

      onChangeSelectedCompetitors(values)
    },
    [selectedCompetitors, onChangeSelectedCompetitors]
  )

  const onConfirmDeleteCompetitor = useCallback(() => {
    if (!onChangeSelectedCompetitors) {
      return
    }
    onChangeSelectedCompetitors(confirmDeleteCompetitorDialog.newItems)
    setConfirmDeleteCompetitorDialog({ isShow: false, newItems: [] })
  }, [onChangeSelectedCompetitors, confirmDeleteCompetitorDialog.newItems])

  const onRejectDeleteCompetitor = useCallback(() => {
    setConfirmDeleteCompetitorDialog({ isShow: false, newItems: [] })
  }, [])

  const onCollapseRow = useCallback(
    (row: ISobCompetitorsGroup) => {
      onCollapse(TABLE_KEY_FIELD, row[TABLE_KEY_FIELD], TABLE_HIERARCHY_FIELD)
    },
    [onCollapse]
  )

  const onExpandRow = useCallback(
    (el: ISobCompetitorsGroup) => {
      onExpand(TABLE_KEY_FIELD, el[TABLE_KEY_FIELD], TABLE_HIERARCHY_FIELD)
    },
    [onExpand]
  )

  if (!data.length) {
    return null
  }

  return (
    <div className={className}>
      <h3 className={styles.title}>{title}</h3>
      {tableBanner}
      <div className={styles.controlsWrapper}>
        <RenderWhen condition={isShowCompetitorsSelect}>
          <div className={styles.controlsWrapper__select}>
            <MultiSelect
              classes={{
                controlClasses: {
                  root: styles.controlsWrapper__select__control,
                },
              }}
              source={competitorsSource}
              value={selectedCompetitors.map((el) => el.CompetitorID)}
              caption={'Name'}
              onChange={onChangeCompetitorsList}
              translation={TRANSLATION_KEYS.selectCompetitors}
              disabled={!isEditableCompetitorSelect}
            />
          </div>
        </RenderWhen>
        {additionalControls}
        <Switch
          label={<Translation text={TRANSLATION_KEYS.sellThruSwitch} />}
          checked={isDisplaySellThru}
          onChange={setIsDisplaySellThru}
        />
      </div>

      <FinancialTable<ISobCompetitorsGroup>
        hierarchyField={TABLE_HIERARCHY_FIELD}
        keyField={TABLE_KEY_FIELD}
        columns={columns}
        data={data}
        headerGroups={headerGroups}
        onCollapse={onCollapseRow}
        onExpand={onExpandRow}
        expandedKeys={expandedKeys}
        withHierarchy
      />
      {saveControl && saveControl({ isDisabledSave: isOtherSobLessThan0 })}

      <SobCompetitorsCharts
        visibleItems={visibleItems}
        selectedPeriodFYear={fYear}
        chartLabelNode={SOB_LABEL}
      />
      <ConfirmDialog
        title={TRANSLATION_KEYS.confirmDeleteCompetitorDialog.title}
        confirmBtnText={TRANSLATION_KEYS.confirmDeleteCompetitorDialog.confirm}
        content={TRANSLATION_KEYS.confirmDeleteCompetitorDialog.text}
        isVisible={confirmDeleteCompetitorDialog.isShow}
        onConfirm={onConfirmDeleteCompetitor}
        onReject={onRejectDeleteCompetitor}
        rejectBtnText={TRANSLATION_KEYS.confirmDeleteCompetitorDialog.cancel}
      />
    </div>
  )
}
